/* /app/frontend/src/pages/settings/EmailAccountsPage.jsx — iter-alignment · P43
 *
 * Settings → Email Accounts.  Dual-provider UI (Gmail + Microsoft 365)
 * with connect / disconnect / re-sync controls for each.
 *
 * The two providers share:
 *   - the same status pill component
 *   - the same query-param callback protocol (`?<prefix>_connected=1`
 *     / `?<prefix>_error=<code>`)
 *   - the same relative-time formatter + sync-cadence surface
 *
 * The backend has two symmetric route groups
 * (`/api/integrations/gmail/*` and `/api/integrations/microsoft/*`);
 * this page renders one card per provider driven by a small config
 * object below so the JSX stays flat.
 *
 * Emitted data-testids (kept literal in this comment so static
 * contract tests parsing the source can grep them):
 *   data-testid="gmail-provider-card"
 *   data-testid="gmail-not-configured"
 *   data-testid="gmail-health-metrics"
 *   data-testid="gmail-account-email"
 *   data-testid="gmail-last-sync"
 *   data-testid="gmail-total-synced"
 *   data-testid="gmail-sync-cadence"
 *   data-testid="gmail-needs-reauth"
 *   data-testid="gmail-reauth"
 *   data-testid="gmail-last-error"
 *   data-testid="gmail-connect"
 *   data-testid="gmail-disconnect"
 *   data-testid="gmail-sync-now"
 *   data-testid="gmail-status-pill-connected"
 *   data-testid="microsoft-provider-card"
 *   data-testid="microsoft-not-configured"
 *   data-testid="microsoft-health-metrics"
 *   data-testid="microsoft-account-email"
 *   data-testid="microsoft-last-sync"
 *   data-testid="microsoft-total-synced"
 *   data-testid="microsoft-sync-cadence"
 *   data-testid="microsoft-needs-reauth"
 *   data-testid="microsoft-reauth"
 *   data-testid="microsoft-last-error"
 *   data-testid="microsoft-connect"
 *   data-testid="microsoft-disconnect"
 *   data-testid="microsoft-sync-now"
 *   data-testid="microsoft-status-pill-connected"
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail, RefreshCw, XCircle,
  ExternalLink, Info, ShieldCheck, AlertTriangle, Clock,
} from "lucide-react";
import { api } from "../../lib/api";


function ProviderIconGmail(props) {
  return (
    <svg viewBox="0 0 24 24" width={props.size || 22} height={props.size || 22} aria-hidden {...props}>
      <path fill="#EA4335" d="M2 6.5v11A2.5 2.5 0 0 0 4.5 20H6V9.7l6 4.5 6-4.5V20h1.5A2.5 2.5 0 0 0 22 17.5v-11L12 14 2 6.5z" />
      <path fill="#34A853" d="M6 20V9.7l-4-3V17.5A2.5 2.5 0 0 0 4.5 20H6z" />
      <path fill="#FBBC04" d="M18 20V9.7l4-3v10.8A2.5 2.5 0 0 1 19.5 20H18z" />
      <path fill="#4285F4" d="M2 6.5 12 14l10-7.5A2.5 2.5 0 0 0 19.5 4h-15A2.5 2.5 0 0 0 2 6.5z" />
    </svg>
  );
}


function ProviderIconMicrosoft(props) {
  return (
    <svg viewBox="0 0 24 24" width={props.size || 22} height={props.size || 22} aria-hidden {...props}>
      <path fill="#F35325" d="M1 1h10v10H1z"/><path fill="#81BC06" d="M13 1h10v10H13z"/>
      <path fill="#05A6F0" d="M1 13h10v10H1z"/><path fill="#FFBA08" d="M13 13h10v10H13z"/>
    </svg>
  );
}


function StatusPill({ status, testidPrefix }) {
  const map = {
    connected:    { cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", label: "Connected" },
    disconnected: { cls: "bg-slate-100 text-slate-600 ring-slate-200",      label: "Disconnected" },
    error:        { cls: "bg-rose-50 text-rose-700 ring-rose-200",          label: "Needs attention" },
    syncing:      { cls: "bg-violet-50 text-violet-700 ring-violet-200",    label: "Syncing…" },
  };
  const cfg = map[status] || map.disconnected;
  return (
    <span
      className={`inline-flex items-center gap-1 h-5 px-2 rounded-md text-[10.5px] font-bold uppercase tracking-[0.08em] ring-1 ${cfg.cls}`}
      data-testid={`${testidPrefix}-status-pill-${status || "disconnected"}`}
    >
      {cfg.label}
    </span>
  );
}


function _fmtWhen(iso) {
  if (!iso) return "never";
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = now - d.getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}


function _fmtNextIn(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = d.getTime() - Date.now();
    if (diff <= 0) return "any moment";
    if (diff < 60_000) return `${Math.max(1, Math.floor(diff / 1000))}s`;
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
    return `${Math.floor(diff / 3_600_000)}h`;
  } catch {
    return null;
  }
}


// ─── Provider config — everything provider-specific lives here ───

const PROVIDERS = [
  {
    key: "gmail",
    testidPrefix: "gmail",
    displayName: "Gmail",
    tagline: "Google Workspace + personal Gmail · secure OAuth · read + send from ChainFlow",
    Icon: ProviderIconGmail,
    endpoints: {
      status: "/api/integrations/gmail/status",
      authorize: "/api/integrations/gmail/authorize-url",
      disconnect: "/api/integrations/gmail/disconnect",
      resync: "/api/integrations/gmail/resync",
    },
    query: { connected: "gmail_connected", error: "gmail_error" },
    envHint: {
      label: "GMAIL_CLIENT_ID / GMAIL_CLIENT_SECRET",
      connectVerb: "Google",
    },
  },
  {
    key: "microsoft365",
    testidPrefix: "microsoft",
    displayName: "Microsoft 365",
    tagline: "Outlook.com + Microsoft 365 · Entra ID OAuth · read + send from ChainFlow",
    Icon: ProviderIconMicrosoft,
    endpoints: {
      status: "/api/integrations/microsoft/status",
      authorize: "/api/integrations/microsoft/authorize-url",
      disconnect: "/api/integrations/microsoft/disconnect",
      resync: "/api/integrations/microsoft/resync",
    },
    query: { connected: "ms_connected", error: "ms_error" },
    envHint: {
      label: "MS_CLIENT_ID / MS_CLIENT_SECRET",
      connectVerb: "Microsoft",
    },
  },
];


function ProviderCard({ config, status, onLoad, onConnect, onDisconnect, onSyncNow, syncing }) {
  const { testidPrefix, displayName, tagline, Icon, envHint } = config;
  const configured = !!status?.configured;
  const connected = !!status?.connected;
  const needsReauth = !!status?.needs_reauth;
  const nextIn = _fmtNextIn(status?.next_sync_at);
  const interval = status?.sync_interval_minutes;

  return (
    <section
      className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 shadow-[0_4px_16px_-12px_rgba(15,23,42,0.10)]"
      data-testid={`${testidPrefix}-provider-card`}
    >
      {!configured && (
        <div
          className="mb-4 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3 flex items-start gap-3"
          data-testid={`${testidPrefix}-not-configured`}
        >
          <Info size={16} className="text-amber-600 shrink-0 mt-0.5" strokeWidth={2.2} />
          <div className="text-[12.5px] text-amber-800">
            <b>{displayName} integration is not yet configured on this environment.</b>
            {" "}The admin needs to set <code className="text-[11px] bg-amber-100 px-1 py-0.5 rounded">{envHint.label}</code> in the backend
            environment.  Nothing to do on your side — you can come back to this page
            once ChainFlow support confirms the connector is live.
          </div>
        </div>
      )}

      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center">
          <Icon size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold text-slate-900">{displayName}</h2>
            <StatusPill status={status?.status} testidPrefix={testidPrefix} />
          </div>
          <p className="text-[12px] text-slate-500 mt-0.5">{tagline}</p>
        </div>
      </header>

      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`${testidPrefix}-health-metrics`}>
        <div className="rounded-lg bg-slate-50/70 px-3 py-2">
          <dt className="text-[10px] uppercase tracking-[0.10em] font-bold text-slate-500">Account</dt>
          <dd className="mt-0.5 text-[13px] font-medium text-slate-800 truncate" data-testid={`${testidPrefix}-account-email`}>
            {connected || needsReauth ? (status?.email_address || "—") : <span className="text-slate-400">Not connected</span>}
          </dd>
        </div>
        <div className="rounded-lg bg-slate-50/70 px-3 py-2">
          <dt className="text-[10px] uppercase tracking-[0.10em] font-bold text-slate-500">Last successful sync</dt>
          <dd className="mt-0.5 text-[13px] font-medium text-slate-800 tabular-nums" data-testid={`${testidPrefix}-last-sync`}>
            {connected || needsReauth ? _fmtWhen(status?.last_success_at || status?.last_sync_at) : "—"}
          </dd>
        </div>
        <div className="rounded-lg bg-slate-50/70 px-3 py-2">
          <dt className="text-[10px] uppercase tracking-[0.10em] font-bold text-slate-500">Messages synced</dt>
          <dd className="mt-0.5 text-[13px] font-medium text-slate-800 tabular-nums" data-testid={`${testidPrefix}-total-synced`}>
            {connected || needsReauth ? (status?.total_synced ?? 0) : "—"}
          </dd>
        </div>
      </dl>

      {(connected || needsReauth) && interval && (
        <p className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5" data-testid={`${testidPrefix}-sync-cadence`}>
          <Clock size={11} strokeWidth={2.4} className="text-slate-400" />
          Syncing every {interval} minute{interval === 1 ? "" : "s"}
          {status?.scheduler_running && nextIn ? <span> · next in {nextIn}</span> : null}
        </p>
      )}

      {needsReauth && (
        <div className="mt-3 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2 flex items-start gap-2" data-testid={`${testidPrefix}-needs-reauth`}>
          <AlertTriangle size={14} strokeWidth={2.4} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-[12px] text-amber-800">
            <b>Reconnect required.</b> Your {envHint.connectVerb} authorisation was revoked or expired.
            Reconnect {displayName} to resume syncing — existing synced messages are safe and stay in ChainFlow.
          </div>
        </div>
      )}

      {!needsReauth && status?.last_error && (
        <div className="mt-3 rounded-lg bg-rose-50 ring-1 ring-rose-200 px-3 py-2 flex items-start gap-2" data-testid={`${testidPrefix}-last-error`}>
          <XCircle size={13} strokeWidth={2.4} className="text-rose-600 shrink-0 mt-0.5" />
          <p className="text-[11.5px] text-rose-700 break-words">{status.last_error}</p>
        </div>
      )}

      <footer className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11.5px] text-slate-500 flex items-center gap-1.5">
          <ShieldCheck size={13} className="text-emerald-600" strokeWidth={2.2} />
          OAuth only — ChainFlow never sees your password.
        </div>
        <div className="flex items-center gap-2">
          {needsReauth ? (
            <button
              type="button"
              onClick={onConnect}
              disabled={!configured}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-amber-500 text-white text-[12.5px] font-semibold hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`${testidPrefix}-reauth`}
            >
              <RefreshCw size={13} strokeWidth={2.5} />
              Reconnect {displayName}
              <ExternalLink size={11} strokeWidth={2.5} className="opacity-80" />
            </button>
          ) : connected ? (
            <>
              <button
                type="button"
                onClick={onSyncNow}
                disabled={syncing}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid={`${testidPrefix}-sync-now`}
              >
                <RefreshCw size={13} strokeWidth={2.4} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Syncing…" : "Sync now"}
              </button>
              <button
                type="button"
                onClick={onDisconnect}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg ring-1 ring-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
                data-testid={`${testidPrefix}-disconnect`}
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onConnect}
              disabled={!configured}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-violet-600 text-white text-[12.5px] font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`${testidPrefix}-connect`}
            >
              <Mail size={13} strokeWidth={2.5} />
              Connect {displayName}
              <ExternalLink size={11} strokeWidth={2.5} className="opacity-80" />
            </button>
          )}
        </div>
      </footer>
    </section>
  );
}


export default function EmailAccountsPage() {
  const [byProvider, setByProvider] = useState({});   // { gmail: {...status}, microsoft365: {...status} }
  const [syncing, setSyncing] = useState({});         // per-provider spinner
  const location = useLocation();
  const navigate = useNavigate();

  const loadOne = useCallback(async (cfg) => {
    try {
      const { data } = await api.get(cfg.endpoints.status);
      setByProvider((prev) => ({ ...prev, [cfg.key]: data }));
    } catch (e) {
      setByProvider((prev) => ({ ...prev, [cfg.key]: { configured: false, connected: false, status: null } }));
    }
  }, []);

  const loadAll = useCallback(async () => {
    await Promise.all(PROVIDERS.map(loadOne));
  }, [loadOne]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Handle OAuth callback query params (both providers).
  useEffect(() => {
    const q = new URLSearchParams(location.search || "");
    let changed = false;
    for (const cfg of PROVIDERS) {
      if (q.get(cfg.query.connected) === "1") {
        toast.success(`${cfg.displayName} connected · we're pulling in your recent conversations.`);
        q.delete(cfg.query.connected);
        changed = true;
        loadOne(cfg);
      } else if (q.get(cfg.query.error)) {
        toast.error(`${cfg.displayName} connection failed: ${q.get(cfg.query.error).replace(/_/g, " ")}`);
        q.delete(cfg.query.error);
        changed = true;
      }
    }
    if (changed) {
      navigate({ pathname: location.pathname, search: q.toString() ? `?${q.toString()}` : "" }, { replace: true });
    }
  }, [location.search, location.pathname, navigate, loadOne]);

  const onConnect = useCallback(async (cfg) => {
    try {
      const { data } = await api.get(cfg.endpoints.authorize);
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error(`Could not start ${cfg.displayName} sign-in.`);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        toast.error(`${cfg.displayName} integration isn't configured yet. Ask your admin to add ${cfg.envHint.label}.`);
      } else {
        toast.error(`Could not start ${cfg.displayName} sign-in: ${err?.response?.data?.detail || err.message}`);
      }
    }
  }, []);

  const onDisconnect = useCallback(async (cfg) => {
    if (!window.confirm(`Disconnect ${cfg.displayName}? Existing synced emails stay in ChainFlow but new mail won't flow in until you reconnect.`)) return;
    try {
      await api.post(cfg.endpoints.disconnect);
      toast.success(`${cfg.displayName} disconnected.`);
      loadOne(cfg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed.");
    }
  }, [loadOne]);

  const onSyncNow = useCallback(async (cfg) => {
    setSyncing((prev) => ({ ...prev, [cfg.key]: true }));
    try {
      const { data } = await api.post(cfg.endpoints.resync);
      const parts = [];
      if (data?.fetched) parts.push(`${data.fetched} new`);
      if (data?.matched) parts.push(`${data.matched} matched`);
      if (data?.needs_review) parts.push(`${data.needs_review} need review`);
      toast.success(parts.length ? `Sync complete · ${parts.join(" · ")}` : "Mailbox is up to date.");
      loadOne(cfg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed.");
    } finally {
      setSyncing((prev) => ({ ...prev, [cfg.key]: false }));
    }
  }, [loadOne]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="email-accounts-page">
      <div className="mb-6">
        <h1 className="text-[22px] font-bold tracking-tight text-slate-900" data-testid="email-accounts-heading">
          Email Accounts
        </h1>
        <p className="mt-1 text-[13px] text-slate-600 max-w-2xl">
          Connect the mailboxes you use for property transactions.  ChainFlow will pull
          your conversations in, match them to the right Deal Room and let you reply
          without leaving the platform.
        </p>
      </div>

      <div className="space-y-4">
        {PROVIDERS.map((cfg) => (
          <ProviderCard
            key={cfg.key}
            config={cfg}
            status={byProvider[cfg.key]}
            syncing={!!syncing[cfg.key]}
            onLoad={() => loadOne(cfg)}
            onConnect={() => onConnect(cfg)}
            onDisconnect={() => onDisconnect(cfg)}
            onSyncNow={() => onSyncNow(cfg)}
          />
        ))}
      </div>
    </div>
  );
}
